import * as React from 'react';

import { Icon } from '../icon/icon.tsx';

import './menu-section.css';
import clsx from 'clsx';

interface MenuSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: 'overview' | 'primary' | 'secondary';
  status?: 'default' | 'warning' | 'error';
  sectionName?: string;
  sectionText?: string;
  sectionPart?: number;
  progress?: number;
  active?: boolean;
  disabled?: boolean;
}

const MenuSection = React.forwardRef<HTMLDivElement, MenuSectionProps>(
  (
    {
      variant,
      status = 'default',
      sectionPart,
      sectionName,
      sectionText,
      progress,
      active,
      disabled,
      ...props
    },
    ref,
  ) => {
    const progressSize = variant === 'primary' ? 48 : 34;
    const menuSectionClasses = clsx('menu-section', {
      [`menu-section-${variant}`]: variant,
      [`menu-section-${status}`]: status,
      [`menu-section-active`]: active,
      [`menu-section-disabled`]: disabled,
    });

    return (
      <div ref={ref} {...props}>
        {variant === 'overview' && (
          <div className="menu-section_overview">
            <Icon size="large" type="arrow-left" />{' '}
            <span>Back to Overview</span>
          </div>
        )}
        {variant !== 'overview' && (
          <div
            className={menuSectionClasses}
            style={
              variant === 'primary'
                ? {
                    borderBottom: '1px solid var(--color_neutral-lighter)',
                  }
                : {}
            }
          >
            <div
              className="menu-section-data-wrapper"
              style={
                variant === 'secondary'
                  ? {
                      borderBottom: '1px solid var(--color_neutral-lighter)',
                    }
                  : {}
              }
            >
              <div className="menu-section-data-wrapper-info">
                {/* The active bar is drawn by .menu-section-active::before rather than a
                    border here, so selecting a section cannot narrow its own text box. */}
                <div className="menu-section-data-wrapper-info-side">
                  {variant === 'primary' && (
                    <div className="menu-section-data-wrapper-info-side-part">
                      PART {sectionPart}
                    </div>
                  )}
                  <div className="menu-section-data-wrapper-info-side-name">
                    {sectionName}
                  </div>
                  {((progress === 100 && status === 'default') ||
                    status !== 'default' ||
                    variant === 'secondary' ||
                    variant === 'primary') && (
                    <div className="menu-section-data-wrapper-info-side-text">
                      {sectionText}
                    </div>
                  )}
                </div>
              </div>
              {status === 'default' &&
                progress !== undefined &&
                progress < 100 && (
                  <div className="progress">
                    {variant === 'primary' && <span>{progress}%</span>}
                    <svg
                      role="img"
                      aria-label="Progress"
                      width={progressSize}
                      height={progressSize}
                      viewBox={`0 0 ${progressSize} ${progressSize}`}
                      className="circular-progress"
                      style={{
                        '--progress': progress,
                        '--size': `${progressSize}px`,
                      }}
                    >
                      <circle className="bg" />
                      <circle className="fg" />
                    </svg>
                  </div>
                )}
              {status === 'warning' && (
                <div className="progress-status">
                  <div className="circle" />
                  <Icon
                    type="warning"
                    size={variant === 'primary' ? 'large' : 'small'}
                    style={{ marginBottom: '1px' }}
                    color={
                      disabled
                        ? 'var(--color_disabled)'
                        : 'var(--color_warning)'
                    }
                  />
                </div>
              )}
              {status === 'error' && (
                <div className="progress-status">
                  <div className="circle" />
                  <Icon
                    type="error"
                    size={variant === 'primary' ? 'large' : 'small'}
                    style={{ marginBottom: '1px' }}
                    color={
                      disabled ? 'var(--color_disabled)' : 'var(--color_error)'
                    }
                  />
                </div>
              )}
              {progress === 100 && status === 'default' && (
                <div className="progress-status">
                  <div className="circle" />
                  <Icon
                    type="success"
                    size={variant === 'primary' ? 'large' : 'small'}
                    style={{ marginBottom: '1px' }}
                    color={
                      disabled
                        ? 'var(--color_disabled)'
                        : 'var(--color_primary-light)'
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export { MenuSection };
